/**
 * feAI – Frontend
 * src/components/auth/LoginForm.tsx
 *
 * Step 1 of the login flow.
 * Single form with "Email o Username" + "Password" fields.
 * Passes credentials up to the parent via onSubmit callback.
 */

import React, { useState, useId } from 'react';
import { Link } from 'react-router-dom';
import { FuturEnergyLogo } from '../brand/FuturEnergyLogo';

interface LoginFormProps {
  onSubmit: (identificativo: string, password: string) => void;
  isLoading: boolean;
  error: string | null;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onSubmit, isLoading, error }) => {
  const [identificativo, setIdentificativo] = useState('');
  const [password, setPassword]             = useState('');
  const [showPwd, setShowPwd]               = useState(false);

  const idId  = useId();
  const pwdId = useId();

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!identificativo.trim() || !password) return;
    onSubmit(identificativo.trim(), password);
  }

  return (
    <>
      {/* ── Brand logo — SVG vettoriale estratto dal PDF ufficiale ── */}
      <div className="login-logo">
        <FuturEnergyLogo
          variant="positive"
          size={200}
        />
      </div>

      <h1 className="login-title">Bentornato</h1>
      <p className="login-subtitle">Accedi al portale agenti FuturEnergy</p>

      {/* ── Error banner ───────────────────────────────────────────────────── */}
      {error && (
        <div className="error-banner" role="alert">
          <span aria-hidden="true">⚠</span>
          {error}
        </div>
      )}

      {/* ── Form ───────────────────────────────────────────────────────────── */}
      <form onSubmit={handleSubmit} noValidate>
        <div className="form-group">
          <label htmlFor={idId} className="form-label">
            Email o Username
          </label>
          <input
            id={idId}
            type="text"
            className="form-input"
            placeholder="nome@azienda.it oppure rossmar001"
            value={identificativo}
            onChange={(e) => setIdentificativo(e.target.value)}
            autoComplete="username"
            autoFocus
            required
            disabled={isLoading}
          />
        </div>

        <div className="form-group">
          <label htmlFor={pwdId} className="form-label">
            Password
          </label>
          <div className="input-wrapper">
            <input
              id={pwdId}
              type={showPwd ? 'text' : 'password'}
              className="form-input"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              disabled={isLoading}
            />
            <button
              type="button"
              className="input-eye"
              onClick={() => setShowPwd((v) => !v)}
              aria-label={showPwd ? 'Nascondi password' : 'Mostra password'}
              tabIndex={-1}
            >
              {showPwd ? '🙈' : '👁'}
            </button>
          </div>
        </div>

        <button
          type="submit"
          className="btn-primary"
          disabled={isLoading || !identificativo.trim() || !password}
        >
          {isLoading ? (
            <><span className="spinner" aria-hidden="true" /> Accesso in corso…</>
          ) : (
            <>Accedi →</>
          )}
        </button>
      </form>

      {/* ── Link "Password dimenticata" ────────────────────────────────────── */}
      <div style={{ textAlign: 'center', marginTop: '1.1rem' }}>
        <Link
          to="/forgot-password"
          style={{
            color:          'var(--text-secondary)',
            fontSize:       '0.85rem',
            textDecoration: 'none',
            transition:     'color 0.2s',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--brand-primary)')}
          onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-secondary)')}
        >
          Password dimenticata?
        </Link>
      </div>
    </>
  );
};
